<script lang="ts" setup>
import { useRouter } from 'vue-router'
import { onMounted, reactive, toRaw, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Product, ProductPrice } from '@/types'
import PriceTable from '@/components/dashboard/Admin/AdminProduct/PriceTable.vue'
import { m2oConvert, o2mConvert, parseToPrimitive, uuid } from '@/composables'
import { useDirtyFormGuard } from '@/composables/useDirtyFormGuard'
import { STATUS_CODES } from '@/constants'
import { useNotifyStore, useProductsStore } from '@/stores'
import { useAxios } from '@/composables/useAxios'
import { useAxiosError } from '@/composables/useAxiosError'
import { DashboardLoadingState } from '@/components/dashboard/common'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

const { t } = useI18n()
const router = useRouter()
const notifyStore = useNotifyStore()
const productsStore = useProductsStore()

const data = reactive({
  loading: false,
  saveLoading: false,
  product: {} as Product,
  productOrig: {} as Product,
  productFeatures: new Map<string, any>(),
  newFeature: { key: '', value: '' }
})

// Track dirty state for form guard
const isDirty = computed(() => {
  // Don't track dirty state for new products or until data is loaded
  if (!data.productOrig.slug) return false
  return JSON.stringify(data.product) !== JSON.stringify(data.productOrig)
})
useDirtyFormGuard('admin-product-edit', isDirty)

type PaymentInterval = 'monthly' | 'quarterly' | 'half-annually' | 'annually'

const payInt: { titleKey: string; value: PaymentInterval }[] = [
  { titleKey: 'dashboard.admin.products.edit.intervals.monthly', value: 'monthly' },
  { titleKey: 'dashboard.admin.products.edit.intervals.quarterly', value: 'quarterly' },
  { titleKey: 'dashboard.admin.products.edit.intervals.halfAnnually', value: 'half-annually' },
  { titleKey: 'dashboard.admin.products.edit.intervals.annually', value: 'annually' }
]

const getSlug = (): string => {
  return `${router.currentRoute.value.params.id}`
}

const isNewProduct = (): boolean => {
  return getSlug() === 'newProd'
}

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

const { handleAxiosError } = useAxiosError()

// Auto-generate slug from name for new products
watch(
  () => data.product.name,
  (newName) => {
    if (isNewProduct() && newName) {
      data.product.slug = generateSlug(newName)
    }
  }
)

// Auto-save when active/public toggles change (only for existing products)
watch(
  () => data.product.active,
  (newValue, oldValue) => {
    // Only save if product was loaded (has original data) and value actually changed
    if (!isNewProduct() && data.productOrig.slug && oldValue !== undefined && newValue !== oldValue) {
      handleSave()
    }
  }
)

watch(
  () => data.product.public,
  (newValue, oldValue) => {
    if (!isNewProduct() && data.productOrig.slug && oldValue !== undefined && newValue !== oldValue) {
      handleSave()
    }
  }
)

const loadProductDetails = () => {
  data.loading = true

  useAxios<null, Product>('GET', `/product/${getSlug()}`, true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validateProduct = Product.safeParse(res.data)
        if (!validateProduct.success) return

        if (validateProduct.data.price) {
          const validatePrice = ProductPrice.array().safeParse(validateProduct.data.price)
          if (!validatePrice.success) return
        }

        data.product = { ...validateProduct.data }
        data.productOrig = { ...validateProduct.data }
        if (validateProduct.data.features) {
          data.productFeatures = o2mConvert(validateProduct.data.features)
        } else {
          data.productFeatures = new Map()
        }
      }
    })
    .catch((error) => {
      handleAxiosError(error)
    })
    .finally(() => {
      data.loading = false
    })
}

const addNewFeature = () => {
  if (!data.productFeatures || !(toRaw(data.productFeatures) instanceof Map)) {
    data.productFeatures = new Map<string, any>()
  }

  // Check for duplicate key
  if (data.productFeatures.has(data.newFeature.key)) {
    notifyStore.createNotify({
      id: uuid(),
      title: t('common.error'),
      message: t('dashboard.admin.products.edit.notifications.featureDuplicate', { key: data.newFeature.key }),
      alertType: 'error'
    })
    return
  }

  data.productFeatures.set(data.newFeature.key, parseToPrimitive(data.newFeature.value))

  handleSave()

  data.newFeature = {
    key: '',
    value: ''
  }
}

const removeFeature = (key: string) => {
  if (data.productFeatures && data.productFeatures.has(key)) {
    data.productFeatures.delete(key)
  }
}

const updateMapVal = (key: string, e: Event) => {
  if (data.productFeatures) {
    data.productFeatures.set(key, (e.target as HTMLInputElement).value)
  }
}

const handleSave = () => {
  data.saveLoading = true

  if (data.productFeatures.size > 0) {
    data.product.features = m2oConvert(data.productFeatures)
  }

  const wasNewProduct = isNewProduct()

  // For new products, don't send slug - let backend generate it
  // For existing products, include slug to update
  let productData: Partial<Product>
  if (wasNewProduct) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { slug, ...rest } = data.product
    productData = rest
  } else {
    productData = data.product
  }

  useAxios<Product, Product>('POST', '/product', true, productData as Product)
    .then((res) => {
      if (!res) return

      if (res.status === STATUS_CODES.OK) {
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: t('dashboard.admin.products.edit.notifications.updated'),
          alertType: 'success'
        })
        // Invalidate products cache so list refreshes
        productsStore.invalidateCache()
      } else if (res.status === STATUS_CODES.CREATED) {
        notifyStore.createNotify({
          id: uuid(),
          title: t('common.success'),
          message: t('dashboard.admin.products.edit.notifications.created'),
          alertType: 'success'
        })
        // Invalidate products cache so list refreshes
        productsStore.invalidateCache()
        // Redirect to the actual product URL using slug from response
        if (wasNewProduct && res.data?.slug) {
          router.replace({
            name: 'dashboardAdminProductEdit',
            params: { id: res.data.slug }
          })
        }
      }
    })
    .catch((error) => {
      handleAxiosError(error)
    })
    .finally(() => {
      data.saveLoading = false
    })
}

const handleRefresh = () => {
  if (!isNewProduct()) {
    loadProductDetails()
  }
}

const handleBack = () => {
  // Invalidate cache so product table refreshes with any changes
  productsStore.invalidateCache()
  router.push({ name: 'dashboardAdminProduct' })
}

onMounted(() => {
  if (!isNewProduct()) {
    loadProductDetails()
  } else {
    data.product.active = true
    data.product.public = true
    data.product.payment_interval = []
    data.product.price = []
  }
})
</script>

<template>
  <BaseDashboardPage
    :title="isNewProduct() ? $t('dashboard.admin.products.edit.newProduct') : $t('dashboard.admin.products.edit.editProduct')"
    :subtitle="isNewProduct() ? $t('dashboard.admin.products.edit.createProduct') : data.product.name || $t('common.loading')"
    :icon="isNewProduct() ? 'mdi-plus' : 'mdi-pencil'"
    icon-color="cyan"
    show-back
    :back-route="{ name: 'dashboardAdminProduct' }"
    @back="handleBack"
  >
    <template #actions>
      <button
        class="save-btn"
        :disabled="data.saveLoading"
        @click.prevent="handleSave"
      >
        <v-progress-circular v-if="data.saveLoading" indeterminate size="18" width="2" />
        <v-icon v-else icon="mdi-content-save" size="18" />
        <span>{{ $t('dashboard.admin.products.edit.save') }}</span>
      </button>
    </template>

    <!-- Loading State -->
    <DashboardLoadingState v-if="data.loading" :message="$t('dashboard.admin.products.edit.loading')" color="cyan" />

    <!-- Content -->
    <template v-else>
      <!-- Status Toggles -->
      <div class="glass-card status-card">
        <h3 class="card-title">{{ $t('dashboard.admin.products.edit.status') }}</h3>
        <div class="status-toggles">
          <label class="toggle-item">
            <input
              type="checkbox"
              v-model="data.product.active"
              class="toggle-input"
            />
            <span class="toggle-switch"></span>
            <span class="toggle-label">{{ $t('dashboard.admin.products.edit.active') }}</span>
            <span class="toggle-desc">{{ $t('dashboard.admin.products.edit.activeDesc') }}</span>
          </label>

          <label class="toggle-item">
            <input
              type="checkbox"
              v-model="data.product.public"
              class="toggle-input"
            />
            <span class="toggle-switch"></span>
            <span class="toggle-label">{{ $t('dashboard.admin.products.edit.public') }}</span>
            <span class="toggle-desc">{{ $t('dashboard.admin.products.edit.publicDesc') }}</span>
          </label>
        </div>
      </div>

      <!-- Basic Info -->
      <div class="glass-card">
        <h3 class="card-title">{{ $t('dashboard.admin.products.edit.basicInfo') }}</h3>
        <div class="form-grid">
          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.products.edit.name') }}</label>
            <input
              v-model="data.product.name"
              type="text"
              class="form-input"
              :placeholder="$t('dashboard.admin.products.edit.namePlaceholder')"
              :autofocus="isNewProduct()"
            />
          </div>

          <div class="form-group">
            <label class="form-label">{{ $t('dashboard.admin.products.edit.slug') }}</label>
            <input
              v-model="data.product.slug"
              type="text"
              class="form-input"
              :placeholder="$t('dashboard.admin.products.edit.slugPlaceholder')"
              :disabled="!isNewProduct()"
            />
          </div>

          <div class="form-group full-width">
            <label class="form-label">{{ $t('dashboard.admin.products.edit.description') }}</label>
            <textarea
              v-model="data.product.description"
              class="form-textarea"
              :placeholder="$t('dashboard.admin.products.edit.descriptionPlaceholder')"
              rows="3"
            ></textarea>
          </div>

          <div class="form-group full-width">
            <label class="form-label">{{ $t('dashboard.admin.products.edit.paymentIntervals') }}</label>
            <div class="interval-chips">
              <label
                v-for="interval in payInt"
                :key="interval.value"
                class="interval-chip"
                :class="{ active: data.product.payment_interval?.includes(interval.value) }"
              >
                <input
                  type="checkbox"
                  :value="interval.value"
                  v-model="data.product.payment_interval"
                  class="chip-input"
                />
                <span class="chip-label">{{ $t(interval.titleKey) }}</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- Prices & Features Grid -->
      <div class="two-column-grid">
        <!-- Prices -->
        <div class="glass-card" :class="{ 'card-disabled': isNewProduct() }">
          <h3 class="card-title">{{ $t('dashboard.admin.products.edit.prices') }}</h3>
          <div v-if="isNewProduct()" class="disabled-notice">
            <v-icon icon="mdi-information-outline" size="20" />
            <span>{{ $t('dashboard.admin.products.edit.pricesSaveFirst') }}</span>
          </div>
          <PriceTable v-else :product="data.product" @refresh="handleRefresh" />
        </div>

        <!-- Features -->
        <div class="glass-card" :class="{ 'card-disabled': isNewProduct() }">
          <h3 class="card-title">{{ $t('dashboard.admin.products.edit.features') }}</h3>
          <div v-if="isNewProduct()" class="disabled-notice">
            <v-icon icon="mdi-information-outline" size="20" />
            <span>{{ $t('dashboard.admin.products.edit.featuresSaveFirst') }}</span>
          </div>
          <div v-else class="features-list">
            <div
              v-for="entry in data.productFeatures.entries()"
              :key="entry[0]"
              class="feature-item"
            >
              <div class="feature-key">
                <v-icon icon="mdi-key" size="16" />
                <span>{{ entry[0] }}</span>
              </div>
              <div class="feature-value-row">
                <input
                  :value="entry[1]"
                  type="text"
                  class="feature-input"
                  @focusout="updateMapVal(entry[0], $event)"
                />
                <button
                  class="delete-feature-btn"
                  :title="$t('dashboard.admin.products.edit.deleteFeature')"
                  @click.prevent="removeFeature(entry[0])"
                >
                  <v-icon icon="mdi-delete" size="18" />
                </button>
              </div>
            </div>

            <!-- Add New Feature -->
            <div class="add-feature">
              <div class="add-feature-row">
                <input
                  v-model="data.newFeature.key"
                  type="text"
                  class="feature-input"
                  :placeholder="$t('dashboard.admin.products.edit.featureKey')"
                />
                <input
                  v-model="data.newFeature.value"
                  type="text"
                  class="feature-input"
                  :placeholder="$t('dashboard.admin.products.edit.featureValue')"
                />
                <button
                  v-if="data.newFeature.key && data.newFeature.value"
                  class="add-feature-btn"
                  :title="$t('dashboard.admin.products.edit.addFeature')"
                  @click.prevent="addNewFeature"
                >
                  <v-icon icon="mdi-plus" size="18" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Save Button */
.save-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 10px;
  color: #4CAF50;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.2);
    border-color: rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

/* Glass Card */
.glass-card {
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
}

.card-title {
  margin: 0 0 20px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}

.card-disabled {
  opacity: 0.7;

  .card-title {
    color: rgba(200, 218, 255, 0.5);
  }
}

.disabled-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.2);
  border-radius: 10px;
  color: rgba(255, 193, 7, 0.9);
  font-size: 0.9rem;

  .v-icon {
    color: rgba(255, 193, 7, 0.8);
    flex-shrink: 0;
  }
}

/* Status Card */
.status-card {
  .card-title {
    margin-bottom: 16px;
  }
}

.status-toggles {
  display: flex;
  gap: 32px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
}

.toggle-input {
  display: none;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 26px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 13px;
  transition: all 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: rgba(200, 218, 255, 0.5);
    border-radius: 50%;
    transition: all 0.2s ease;
  }
}

.toggle-input:checked + .toggle-switch {
  background: rgba(76, 175, 80, 0.2);
  border-color: rgba(76, 175, 80, 0.4);

  &::after {
    left: 25px;
    background: #4CAF50;
  }
}

.toggle-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.toggle-desc {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Form */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &.full-width {
    grid-column: 1 / -1;
  }
}

.form-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
}

.form-input,
.form-textarea {
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 176, 255, 0.4);
    background: rgba(255, 255, 255, 0.05);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
}

/* Interval Chips */
.interval-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.interval-chip {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.3);

    .chip-label {
      color: #00B0FF;
    }
  }
}

.chip-input {
  display: none;
}

.chip-label {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.7);
  transition: color 0.2s ease;
}

/* Two Column Grid */
.two-column-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
}

/* Features */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
}

.feature-key {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);

  .v-icon {
    color: rgba(0, 176, 255, 0.6);
  }
}

.feature-value-row {
  display: flex;
  gap: 8px;
}

.feature-input {
  flex: 1;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.12);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    outline: none;
    border-color: rgba(0, 176, 255, 0.3);
  }
}

.delete-feature-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  color: #F44336;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(244, 67, 54, 0.15);
    border-color: rgba(244, 67, 54, 0.3);
  }
}

.add-feature {
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.add-feature-row {
  display: flex;
  gap: 8px;
}

.add-feature-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 8px;
  color: #4CAF50;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(76, 175, 80, 0.15);
    border-color: rgba(76, 175, 80, 0.3);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .two-column-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .status-toggles {
    flex-direction: column;
    gap: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .save-btn,
  .toggle-switch,
  .toggle-switch::after,
  .form-input,
  .form-textarea,
  .interval-chip,
  .chip-label,
  .feature-input,
  .delete-feature-btn,
  .add-feature-btn {
    transition: none;
  }
}
</style>
